import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings2, Check, Ban } from 'lucide-react';
import { logger } from '../lib/logger';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../lib/api';

/**
 * CookieBanner — 3-tier GDPR-compliant cookie consent banner.
 *
 * ★ 2026-08-27 (Phase 57.A) — Riscritto per compliance GDPR + CCPA + LGPD.
 *
 * Struttura:
 *   • Prima visita → mostra banner in basso con 3 CTA:
 *     - Accept all (analytics + marketing)
 *     - Reject all (solo tecnici)
 *     - Customize (apre modale preferenze)
 *   • Modale Customize → 3 switch per categoria (technical locked ON):
 *     - Technical (essenziali, non revocabili)
 *     - Analytics (default OFF, richiede consenso)
 *     - Marketing (default OFF, richiede consenso)
 *   • Persistenza: localStorage 'gp_consent_v1' = {
 *       version, timestamp, prefs: {technical, analytics, marketing}, action
 *     }
 *   • Audit: POST /api/public/consent con IP+UA+lang+prefs
 *   • Trigger event 'gp:open-cookie-prefs' apre il modale (usato da
 *     CookiePolicyPage e footer).
 *
 * Compliance notes:
 *   • Reject-all deve essere visibile quanto Accept-all (Garante Privacy).
 *   • Nessun tracker deve caricarsi prima del consenso marketing.
 *   • La scelta va salvata con timestamp + versione policy per audit trail.
 */
const LS_KEY = 'gp_consent_v1';
const POLICY_VERSION = '1.0.0-2026-08-27';

function readConsent() {
  // ★ Audit sicurezza (Round 2): localStorage OK — il consenso cookie è una
  // preferenza non sensibile che per GDPR DEVE persistere tra sessioni.
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeConsent(payload) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  } catch { /* quota exceeded, ignore */ }
}

/**
 * Espone il consenso corrente all'app (utile per gating trackers).
 * Uso: import { getConsent } from '@/components/CookieBanner';
 */
export function getConsent() {
  const c = readConsent();
  return c?.prefs || { technical: true, analytics: false, marketing: false };
}

export default function CookieBanner() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [openPrefs, setOpenPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      // Rehydrate switches for the Customize modal
      setAnalytics(!!existing.prefs?.analytics);
      setMarketing(!!existing.prefs?.marketing);
    }
  }, []);

  // Listen for global "open cookie prefs" event fired by CookiePolicyPage / footer link.
  useEffect(() => {
    const handler = () => setOpenPrefs(true);
    window.addEventListener('gp:open-cookie-prefs', handler);
    return () => window.removeEventListener('gp:open-cookie-prefs', handler);
  }, []);

  const persist = useCallback(async (prefs, action) => {
    const lang = (i18n.language || 'it').split('-')[0];
    const payload = {
      version: POLICY_VERSION,
      timestamp: new Date().toISOString(),
      prefs,
      action,
      lang,
    };
    writeConsent(payload);
    // Fire-and-forget audit trail
    try {
      await api.post('/public/consent', {
        technical: true,
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
        action,
        lang,
        source_page: typeof window !== 'undefined' ? window.location.pathname : '/',
      });
    } catch (e) {
      logger.warn('cookie consent audit failed', e);
    }
    // Notify the rest of the app so trackers can bootstrap themselves.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('gp:consent-updated', { detail: payload }));
    }
  }, [i18n.language]);

  const acceptAll = () => {
    persist({ technical: true, analytics: true, marketing: true }, 'accept_all');
    setVisible(false);
    setOpenPrefs(false);
  };
  const rejectAll = () => {
    persist({ technical: true, analytics: false, marketing: false }, 'reject_all');
    setVisible(false);
    setOpenPrefs(false);
  };
  const savePrefs = () => {
    persist({ technical: true, analytics, marketing }, 'save');
    setVisible(false);
    setOpenPrefs(false);
  };

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label={t('cookie.ariaLabel', 'Preferenze cookie')}
          className="fixed bottom-2 left-2 right-2 md:left-4 md:right-4 md:max-w-2xl md:mx-auto md:bottom-4 z-[80] bg-card/95 backdrop-blur-md border border-border rounded-2xl shadow-2xl p-3 md:p-5"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <div className="hidden sm:grid w-10 h-10 rounded-full bg-[hsl(var(--accent))] place-items-center shrink-0">
              <Cookie className="w-5 h-5 text-[hsl(var(--primary))]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm md:text-base" data-testid="cookie-banner-title">
                {t('cookie.title', 'La tua privacy conta')}
              </div>
              <p className="text-[11px] sm:text-xs md:text-[13px] text-muted-foreground mt-1 leading-snug sm:leading-relaxed line-clamp-3 sm:line-clamp-none" data-testid="cookie-banner-body">
                {t('cookie.body', '')}
              </p>
              <div className="mt-2.5 sm:mt-3 flex flex-wrap gap-2 items-center">
                <Button size="sm" onClick={acceptAll} data-testid="cookie-accept" className="flex-1 sm:flex-none">
                  <Check className="w-3.5 h-3.5 mr-1.5" /> {t('cookie.acceptAll', 'Accetta tutti')}
                </Button>
                <Button size="sm" variant="secondary" onClick={rejectAll} data-testid="cookie-reject" className="flex-1 sm:flex-none">
                  <Ban className="w-3.5 h-3.5 mr-1.5" /> {t('cookie.rejectAll', 'Rifiuta tutti')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpenPrefs(true)} data-testid="cookie-customize">
                  <Settings2 className="w-3.5 h-3.5 mr-1.5" /> {t('cookie.customize', 'Personalizza')}
                </Button>
              </div>
              <div className="mt-2 sm:mt-3 text-[11px] text-muted-foreground flex flex-wrap gap-3">
                <Link to="/privacy" className="underline hover:text-foreground" data-testid="cookie-banner-privacy-link">
                  {t('cookie.privacyLink', 'Privacy Policy')}
                </Link>
                <Link to="/cookie-policy" className="underline hover:text-foreground" data-testid="cookie-banner-cookie-link">
                  {t('cookie.moreInfo', 'Cookie Policy')}
                </Link>
              </div>
            </div>
            <button
              onClick={rejectAll}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t('cookie.close', 'Chiudi')}
              data-testid="cookie-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={openPrefs} onOpenChange={setOpenPrefs}>
        <DialogContent className="max-w-lg" data-testid="cookie-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> {t('cookie.customize', 'Personalizza')}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {t('cookie.body', '')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Technical */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{t('cookie.tiers.technicalTitle', 'Cookie tecnici (essenziali)')}</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t('cookie.tiers.technicalDesc', '')}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('cookie.tiers.technicalBadge', 'Sempre attivi')}</span>
                <Switch checked disabled data-testid="cookie-toggle-technical" />
              </div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{t('cookie.tiers.analyticsTitle', 'Cookie di analisi')}</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t('cookie.tiers.analyticsDesc', '')}</p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid="cookie-toggle-analytics"
              />
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-foreground">{t('cookie.tiers.marketingTitle', 'Cookie di marketing')}</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t('cookie.tiers.marketingDesc', '')}</p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                data-testid="cookie-toggle-marketing"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={rejectAll} data-testid="cookie-prefs-reject">
                <Ban className="w-3.5 h-3.5 mr-1.5" /> {t('cookie.rejectAll', 'Rifiuta tutti')}
              </Button>
              <Button size="sm" onClick={acceptAll} data-testid="cookie-prefs-accept-all">
                <Check className="w-3.5 h-3.5 mr-1.5" /> {t('cookie.acceptAll', 'Accetta tutti')}
              </Button>
            </div>
            <Button size="sm" variant="default" onClick={savePrefs} data-testid="cookie-prefs-save">
              {t('cookie.savePrefs', 'Salva preferenze')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
